import type { Sell } from "../sell.entity";
import type { Product } from "../../product/product.entity";
import type { CopilotInsightCard, CopilotQuickAction } from "../../ai/callable-ai";
import { SellUnregisterProduct } from "../sell-unregister-product";

export type CartItem = {
  productId: string;
  name: string;
  price: number;
  quantity: number;
  categoryId: string | null;
};

export type CartSummary = {
  items: CartItem[];
  total: number;
  itemCount: number;
};

export function extractCartSummary(sell: Sell): CartSummary {
  const items = sell.getProducts().getValues().map((p) => ({
    productId: p.entity instanceof SellUnregisterProduct ? "" : p.entity.id,
    name: p.entity.getName() || "Produto",
    price: p.price,
    quantity: p.quantity,
    categoryId:
      p.entity instanceof SellUnregisterProduct
        ? null
        : (p.entity.getCategory()?.id ?? null),
  }));
  const total = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const itemCount = items.reduce((s, i) => s + i.quantity, 0);
  return { items, total, itemCount };
}

function scoreCatalogItem(product: Product, cart: CartSummary): number {
  let score = 0;

  // Produtos da mesma categoria recebem peso maior (cross-sell natural)
  const cartCatIds = new Set(cart.items.map((i) => i.categoryId).filter(Boolean));
  const itemCatId = product.getCategory()?.id ?? null;
  if (itemCatId && cartCatIds.has(itemCatId)) score += 4;

  // Compatibilidade de preço: dentro de 3x do ticket médio
  const avgPrice = cart.total / Math.max(cart.itemCount, 1);
  if (avgPrice > 0) {
    const ratio = product.getPrice() / avgPrice;
    if (ratio >= 0.2 && ratio <= 3.0) score += 2;
  }

  // Produtos com estoque disponível têm preferência leve
  if (product.getStock() > 0) score += 1;

  return score;
}

export function computeInsights(
  cart: CartSummary,
  catalog: Product[]
): CopilotInsightCard[] {
  if (cart.items.length === 0) return [];

  const cartIds = new Set(cart.items.map((i) => i.productId).filter(Boolean));

  const scored = catalog
    .filter((p) => !cartIds.has(p.id) && p.getPrice() > 0)
    .map((p) => ({ product: p, score: scoreCatalogItem(p, cart) }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 3);

  return scored.map(({ product }) => ({
    productId: product.id,
    title: product.getName(),
    subtitle: product.getPrice().toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    }),
  }));
}

function nextRoundTarget(total: number): number | null {
  const targets = [5, 10, 20, 25, 50, 100, 150, 200, 250, 500];
  for (const t of targets) {
    if (total < t) return t;
  }
  const nearest50 = Math.ceil(total / 50) * 50;
  return nearest50 > total ? nearest50 : null;
}

export function computeQuickActions(cart: CartSummary): CopilotQuickAction[] {
  if (cart.items.length === 0) return [];

  const actions: CopilotQuickAction[] = [];

  // Desconto por valor
  if (cart.total >= 20) {
    const pct = cart.total >= 200 ? 15 : cart.total >= 100 ? 10 : 5;
    actions.push({
      title: `Dar ${pct}% de desconto`,
      pdvAction: "apply_discount",
      discountPercent: pct,
      confirm: true,
    });
  }

  // Arredondar total para próximo valor limpo
  const roundTarget = nextRoundTarget(cart.total);
  if (roundTarget !== null && roundTarget - cart.total <= cart.total * 0.12) {
    actions.push({
      title: `Arredondar para R$ ${roundTarget.toFixed(0)}`,
      pdvAction: "round_total",
      roundTarget,
      confirm: false,
    });
  }

  // Duplicar último item adicionado
  if (cart.items.length > 0) {
    const last = cart.items[cart.items.length - 1];
    actions.push({
      title: `Mais um "${last.name.slice(0, 20)}"`,
      pdvAction: "duplicate_last_item",
      confirm: false,
    });
  }

  // Fiado
  actions.push({
    title: "Lançar como fiado",
    pdvAction: "mark_as_credit",
    confirm: true,
  });

  // Remover último: só quando há mais de 1 item
  if (cart.items.length > 1) {
    actions.push({
      title: "Remover último item",
      pdvAction: "remove_last_item",
      confirm: false,
    });
  }

  // Limpar carrinho: quando há itens suficientes
  if (cart.items.length >= 3) {
    actions.push({
      title: "Limpar carrinho",
      pdvAction: "clear_cart",
      confirm: true,
    });
  }

  return actions.slice(0, 5);
}
